import { Children, useRef, type ReactNode } from "react";
import { useReactFlow } from "@xyflow/react";

/**
 * SectionBox — THE board section-container standard (Erez-locked 2026-07-19).
 *
 * Every section on every board is one of these, and NOTHING lives outside one.
 * It hugs its content automatically: `display:inline-block` + a big equal
 * padding means the box sizes itself to whatever is inside — auto width AND
 * height, a tall card just makes it taller; never guess dimensions. Neutral
 * gray (a step darker than the canvas), with ONE big black label INSIDE at the
 * top. No subtitles, no floating titles; individual cards carry no titles of
 * their own — this label is the only text label.
 *
 * Single source of truth: change the look HERE and every board updates.
 */

export const SECTION_BG = "#D2D2D2";
export const SECTION_BORDER = "#BEBEBE";
export const SECTION_INK = "#1C1917";
const FONT = "Inter, system-ui, -apple-system, sans-serif";

/**
 * SectionGrid — arranges a board's SectionBoxes into a packed GRID, not a single
 * row or column (Erez-locked 2026-07-19). Uses CSS multi-column: the boxes flow
 * DOWN the first column, then the next — so short sections stack under each other
 * and the whole board reads as a compact rectangle. Put ALL of a board's
 * SectionBoxes inside one SectionGrid (which lives in one React Flow node).
 */
export function SectionGrid({
  children,
  gap = 200,
  width = 24000,
}: {
  children: ReactNode;
  gap?: number;
  /** FIXED board width — flex-wrap fills it and packs sections into rows, keeping the board a WIDE rectangle. */
  width?: number;
}) {
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "flex-start",
        alignContent: "flex-start",
        gap,
        width,
      }}
    >
      {Children.map(children, (c) => c)}
    </div>
  );
}

export function SectionBox({
  title,
  children,
  padding = 210,
  gap = 64,
  layout = "row",
}: {
  /** The one label for this section (big black pill, white text). */
  title: string;
  /** The section's cards — already the real content; no titles of their own. */
  children: ReactNode;
  padding?: number;
  /** Gap between children when layout = "row"/"column". */
  gap?: number;
  /** "row" (default) and "column" auto-lay the children; "custom" = you arrange them. */
  layout?: "row" | "column" | "custom";
}) {
  // ── HOVER-TO-FOCUS (Erez, 2026-07-24) ──────────────────────────────────────
  // Hover the label ~400ms → the camera flies to frame this section's CONTENT
  // (the app/cards) edge-to-edge with a little padding — NOT the big gray box
  // around it (an app "has its own section", so frame the app itself). Click the
  // label → snap even tighter. Every board inherits this because every section is
  // a SectionBox. The camera moves; the content doesn't change ("focus", not
  // "semantic zoom").
  const contentRef = useRef<HTMLDivElement | null>(null);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rf = useReactFlow();

  const frame = (pad: number) => {
    const el = contentRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const tl = rf.screenToFlowPosition({ x: r.left, y: r.top });
    const br = rf.screenToFlowPosition({ x: r.right, y: r.bottom });
    rf.fitBounds(
      { x: tl.x, y: tl.y, width: br.x - tl.x, height: br.y - tl.y },
      { duration: 650, padding: pad },
    );
  };

  const armHover = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => frame(0.045), 400);
  };
  const cancelHover = () => {
    if (hoverTimer.current) {
      clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
    }
  };

  // ⛔ ROOT CAUSE, FOUND AND FIXED HERE 24 Aug 2026 (verified against React
  // Flow's own source, not guessed): a non-draggable node — every board node
  // is, via `nodesDraggable={false}` in BoardCanvas — gets `pointer-events:
  // none` set INLINE by React Flow itself, so panning still works with the
  // cursor over a section. That property INHERITS to every descendant unless
  // something re-enables it. This file already did that for the section
  // TITLE label (below, its own `pointerEvents: "auto"`) but never for the
  // content area — so nothing placed inside a SectionBox could ever receive
  // a real click unless the component patched itself. DocumentFrame found
  // and worked around this for itself in 2026 (see its own header comment);
  // that was a symptom fix, never the source. This is the source.
  const contentPointerFix = { pointerEvents: "auto" as const };

  const content =
    layout === "custom" ? (
      // data-zoom: double-clicking this section frames it (BoardCanvas). A
      // marked child (a card, a document sheet) wins via closest(), so the
      // section is the fallback target, not the only one.
      <div ref={contentRef} data-zoom style={{ marginTop: 96, ...contentPointerFix }}>{children}</div>
    ) : (
      <div
        ref={contentRef}
        data-zoom
        style={{
          display: "flex",
          flexDirection: layout === "column" ? "column" : "row",
          alignItems: "flex-start",
          gap,
          marginTop: 96,
          ...contentPointerFix,
        }}
      >
        {children}
      </div>
    );

  return (
    <div
      style={{
        display: "inline-block",
        background: SECTION_BG,
        border: `1px solid ${SECTION_BORDER}`,
        borderRadius: 48,
        padding,
        fontFamily: FONT,
      }}
    >
      <span
        className="nopan nodrag"
        onMouseEnter={armHover}
        onMouseLeave={cancelHover}
        onClick={() => { cancelHover(); frame(0.02); }}
        onPointerDown={(e) => e.stopPropagation()}
        title="Hover to frame · click to get closer"
        style={{
          display: "inline-block",
          fontSize: 112,
          fontWeight: 800,
          letterSpacing: "-0.02em",
          color: "#FFFFFF",
          background: SECTION_INK,
          borderRadius: 22,
          padding: "16px 52px",
          lineHeight: 1.14,
          cursor: "pointer",
          pointerEvents: "auto",
          userSelect: "none",
        }}
      >
        {title}
      </span>
      {content}
    </div>
  );
}
